import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

/**
 * Thin CRUD service for reference resources without client-side Zod validation.
 */
export class SimpleCrudService {
    /**
     * @param {import('../api/base.api.js').BaseApi} api
     * @param {{ listMessage?: string }} [config]
     */
    constructor(api, config = {}) {
        this.api = api;
        this.listMessage = config.listMessage || 'Liste getirildi';
    }

    async getAll(params = {}) {
        try {
            const body = await withRetry(() => this.api.getAll(params), { maxRetries: 2 });
            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                uniqueValues: body.uniqueValues || {},
                message: body.message || this.listMessage,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch list');
        }
    }

    async getById(id) {
        try {
            const response = await withRetry(() => this.api.getById(id), { maxRetries: 2 });
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch record');
        }
    }

    async create(data) {
        try {
            const response = await this.api.create(data);
            return { success: true, data: response.data, message: response.message || 'Kayıt oluşturuldu' };
        } catch (error) {
            throw this._handleError(error, 'Failed to create');
        }
    }

    async update(id, data) {
        try {
            const response = await this.api.update(id, data);
            return { success: true, data: response.data, message: response.message || 'Kayıt güncellendi' };
        } catch (error) {
            throw this._handleError(error, 'Failed to update');
        }
    }

    async delete(id) {
        try {
            const response = await this.api.delete(id);
            return { success: true, data: response.data, message: response.message || 'Kayıt silindi' };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete');
        }
    }

    async batchUpdate(ids, patch) {
        try {
            const response = await withRetry(() => this.api.batchUpdate(ids, patch), { maxRetries: 2 });
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to batch update');
        }
    }

    async batchDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);
            return { success: true, data: response.data, message: response.message || 'Kayıtlar silindi' };
        } catch (error) {
            throw this._handleError(error, 'Failed to batch delete');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) throw error;
        if (error.statusCode || error.status) throw ApiError.fromResponse(error);
        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
